/**
 * Move-structure proposal card (the runtime HITL gate). The move edits the
 * structure IN PLACE, like the Transform tool's move. Pure presentational:
 * it shows the RESOLVED target, the patient direction and the distance in
 * cm (with a note when the request was rounded to the clinical grid), the
 * centre of mass before and after (patient X/Y/Z, cm) and the room left
 * before the scan edge. Hovering the card previews the moved structure in
 * the viewer. Frame, status chip, hover preview and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import type { MoveProposal } from '~/agent';
import { formatCmTrimmed, formatCmWithUnit } from '~/utils/units';
import GridRoundingNote from './GridRoundingNote';
import ProposalCardShell, {
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

const DIRECTION_LABEL: Record<MoveProposal['direction'], string> = {
    anterior: 'anterior (towards the front)',
    posterior: 'posterior (towards the back)',
    left: "to the patient's left",
    right: "to the patient's right",
    superior: 'superior (towards the head)',
    inferior: 'inferior (towards the feet)',
};

/** A patient position in cm. */
export interface PositionCm {
    readonly x: number;
    readonly y: number;
    readonly z: number;
}

const fmt = (p: PositionCm) =>
    `X ${p.x.toFixed(2)}, Y ${p.y.toFixed(2)}, Z ${p.z.toFixed(2)} cm`;

export interface MoveStructureProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly direction: MoveProposal['direction'];
    readonly distanceCm: number;
    readonly requestedDistanceCm?: number;
    readonly currentCentreCm?: PositionCm;
    readonly newCentreCm?: PositionCm;
    readonly roomLeftCm?: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function MoveStructureProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    direction,
    distanceCm,
    requestedDistanceCm,
    currentCentreCm,
    newCentreCm,
    roomLeftCm,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: MoveStructureProposalCardProps) {
    return (
        <ProposalCardShell
            title="Move structure"
            ariaLabel="Move structure proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                move “{targetName}” {formatCmWithUnit(distanceCm)}{' '}
                {DIRECTION_LABEL[direction]}
            </ProposalSummaryBox>

            {requestedDistanceCm !== undefined && (
                <GridRoundingNote>
                    Rounded from {formatCmTrimmed(requestedDistanceCm)} cm to
                    the 0.1 mm grid.
                </GridRoundingNote>
            )}

            {currentCentreCm && newCentreCm && (
                <Typography variant="caption" color="text.secondary">
                    Centre: {fmt(currentCentreCm)} →{' '}
                    <strong>{fmt(newCentreCm)}</strong>
                </Typography>
            )}
            {roomLeftCm !== undefined && (
                <Typography variant="caption" color="text.secondary">
                    Room left before the scan edge: {roomLeftCm.toFixed(2)} cm
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
                Hover over this card to preview the moved structure.
            </Typography>

            <Typography variant="caption" color="text.secondary">
                This moves “{targetName}” itself, like the Transform tool; its
                shape and size do not change. Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
